import React, { useEffect, useState } from 'react';
import { Row, Section } from '../settingsPrimitives';
import { getApibooStatus, peekApibooStatus } from '../../services/ai/apibooStatus';
import '../aiChat/aiApiboo.css';

/**
 * Settings → AI Chat when the Apiboo assistant is the selected provider
 * (22.08.2026). Shown INSTEAD of the API-key section: there is no key to
 * manage — the server holds one. What the user needs to know is which model
 * answers, how much of the monthly quota is left, and what leaves the device
 * (GDPR Art. 13: the same words as the first-use notice, standing).
 */
function formatTokens(n) {
  if (!Number.isFinite(n)) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}k`;
  return String(n);
}

function AiApibooSection({ t }) {
  const [status, setStatus] = useState(peekApibooStatus);

  useEffect(() => {
    let stale = false;
    getApibooStatus({ force: true }).then((s) => { if (!stale) setStatus(s); });
    return () => { stale = true; };
  }, []);

  const quota = status?.quota;
  const limit = quota?.limit;
  const quotaText = quota
    ? limit == null || limit < 0
      ? t('settings.aiChatApibooQuotaUnlimited', { used: formatTokens(quota.used) })
      : t('settings.aiChatApibooQuota', { used: formatTokens(quota.used), limit: formatTokens(limit) })
    : '—';

  return (
    <Section title={t('settings.aiChatApibooSection')}>
      {status && !status.available && (
        <div className="ai-apiboo-unavailable" data-testid="ai-apiboo-unavailable">
          {status.reason === 'unauthenticated'
            ? t('settings.aiChatApibooSignIn')
            : t('settings.aiChatApibooUnavailable')}
        </div>
      )}
      <Row label={t('settings.aiChatApibooModel')} desc={t('settings.aiChatApibooIncluded')}>
        <span className="settings-row-desc" data-testid="ai-apiboo-model">{status?.model || '—'}</span>
      </Row>
      <Row label={t('settings.aiChatApibooQuotaLabel')} desc={t('settings.aiChatApibooQuotaDesc')}>
        <span className="settings-row-desc" data-testid="ai-apiboo-quota">{quotaText}</span>
      </Row>
      {status?.per_minute > 0 && (
        <Row label={t('settings.aiChatApibooPerMinute')}>
          <span className="settings-row-desc">{status.per_minute}</span>
        </Row>
      )}
      <p className="ai-apiboo-privacy" data-testid="ai-apiboo-privacy">{t('settings.aiChatApibooPrivacy')}</p>
      {/* web_search / fetch_url (22.08.2026): what leaves the device for the
          two discovery tools, in the same standing place as the assistant's
          own disclosure. The search row only renders when the server says
          whether it has a search engine — an older server says nothing. */}
      <p className="ai-apiboo-privacy" data-testid="ai-discovery-privacy">{t('settings.aiChatDiscoveryPrivacy')}</p>
      {status?.search_available === false && (
        <Row label={t('settings.aiChatWebSearchLabel')}>
          <span className="settings-row-desc" data-testid="ai-web-search-unavailable">
            {t('settings.aiChatWebSearchUnavailable')}
          </span>
        </Row>
      )}
      <p className="settings-row-desc">{t('settings.aiChatApibooUseOwnKey')}</p>
    </Section>
  );
}

export default AiApibooSection;
